import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingTable = ({ activeTab, data, onSelectRecord, selectedRecords }) => {
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: 'bg-success/20 text-success',
      pending: 'bg-warning/20 text-warning',
      overdue: 'bg-error/20 text-error',
      failed: 'bg-error/20 text-error',
      refunded: 'bg-text-muted/20 text-text-muted'
    };
    return colors[status] || 'bg-text-muted/20 text-text-muted';
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'credit-card': 'CreditCard',
      'bank-transfer': 'Building2',
      'paypal': 'Wallet',
      'stripe': 'CreditCard',
      'cash': 'Banknote'
    };
    return icons[method] || 'CreditCard';
  };

  const renderTableHeaders = () => {
    const commonHeaders = [
      { key: 'select', label: '', sortable: false },
      { key: 'invoiceId', label: 'Invoice ID', sortable: true },
      { key: 'amount', label: 'Amount', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'dueDate', label: 'Due Date', sortable: true },
      { key: 'paymentMethod', label: 'Payment Method', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ];

    const specificHeaders = {
      'per-card': [
        { key: 'cardId', label: 'Card ID', sortable: true },
        { key: 'cardName', label: 'Card Name', sortable: true }
      ],
      'per-user': [
        { key: 'userName', label: 'User Name', sortable: true },
        { key: 'userEmail', label: 'Email', sortable: true }
      ],
      'team-billing': [
        { key: 'teamName', label: 'Team Name', sortable: true },
        { key: 'memberCount', label: 'Members', sortable: true }
      ]
    };

    const headers = [
      ...commonHeaders.slice(0, 2),
      ...specificHeaders[activeTab],
      ...commonHeaders.slice(2)
    ];

    return headers.map((header) => (
      <th
        key={header.key}
        className={`px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider ${
          header.sortable ? 'cursor-pointer hover:text-text-primary' : ''
        }`}
        onClick={header.sortable ? () => handleSort(header.key) : undefined}
      >
        <div className="flex items-center space-x-1">
          <span>{header.label}</span>
          {header.sortable && sortField === header.key && (
            <Icon
              name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
              size={14}
            />
          )}
        </div>
      </th>
    ));
  };

  const renderTableRow = (record) => {
    const isSelected = selectedRecords.includes(record.id);

    return (
      <tr
        key={record.id}
        className={`hover:bg-hover transition-colors duration-150 ${
          isSelected ? 'bg-accent/10' : ''
        }`}
      >
        {/* Select Checkbox */}
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelectRecord(record.id)}
            className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent focus:ring-2"
          />
        </td>

        {/* Invoice ID */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-text-primary font-mono">
            {record.invoiceId}
          </div>
        </td>

        {/* Specific Fields Based on Tab */}
        {activeTab === 'per-card' && (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-text-primary font-mono">{record.cardId}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-text-primary">{record.cardName}</div>
            </td>
          </>
        )}

        {activeTab === 'per-user' && (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-text-primary">{record.userName}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-text-muted">{record.userEmail}</div>
            </td>
          </>
        )}

        {activeTab === 'team-billing' && (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-text-primary">{record.teamName}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-text-muted">{record.memberCount} members</div>
            </td>
          </>
        )}

        {/* Amount */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-text-primary font-mono">
            ${record.amount.toFixed(2)}
          </div>
        </td>

        {/* Status */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record.status)}`}>
            {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
          </span>
        </td>

        {/* Due Date */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-text-primary">
            {new Date(record.dueDate).toLocaleDateString()}
          </div>
        </td>

        {/* Payment Method */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            <Icon name={getPaymentMethodIcon(record.paymentMethod)} size={16} className="text-text-muted" />
            <span className="text-sm text-text-muted capitalize">
              {record.paymentMethod.replace('-', ' ')}
            </span>
          </div>
        </td>

        {/* Actions */}
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center space-x-2">
            <Button variant="ghost" className="p-1">
              <Icon name="Eye" size={16} />
            </Button>
            <Button variant="ghost" className="p-1">
              <Icon name="Download" size={16} />
            </Button>
            <Button variant="ghost" className="p-1">
              <Icon name="MoreHorizontal" size={16} />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-background">
            <tr>
              {renderTableHeaders()}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.map(renderTableRow)}
          </tbody>
        </table>
      </div>

      {data.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Receipt" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No billing records found</h3>
          <p className="text-text-muted">
            Try adjusting your filters or search criteria to find billing records.
          </p>
        </div>
      )}
    </div>
  );
};

export default BillingTable;